import { useState } from 'react';
import { HandThumbUpIcon as HandThumbUpOutline } from '@heroicons/react/24/outline';
import { HandThumbUpIcon as HandThumbUpSolid } from '@heroicons/react/24/solid';
import { scoringApi } from '@/services/api';

interface VoteButtonProps {
  initiativeId: string;
  count: number;
  hasVoted: boolean;
  onChange?: (count: number, hasVoted: boolean) => void;
  size?: 'sm' | 'md';
}

/**
 * Thumbs-up vote toggle. Open to ALL authenticated users (incl. viewers) —
 * an intentional exception to the write-permission rule. Optimistic update with
 * rollback on failure.
 */
export function VoteButton({
  initiativeId,
  count,
  hasVoted,
  onChange,
  size = 'md',
}: VoteButtonProps) {
  const [busy, setBusy] = useState(false);
  const iconSize = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);

    // Optimistic update
    const nextVoted = !hasVoted;
    const nextCount = count + (nextVoted ? 1 : -1);
    onChange?.(nextCount, nextVoted);

    try {
      const result = await scoringApi.vote(initiativeId);
      onChange?.(result.count, result.hasVoted);
    } catch (err) {
      console.error('Failed to toggle vote:', err);
      onChange?.(count, hasVoted); // revert
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      title={hasVoted ? 'Remove your vote' : 'Vote for this initiative'}
      className={`inline-flex items-center gap-1 rounded-md px-1.5 py-1 transition-colors ${
        hasVoted
          ? 'text-blue-600 hover:bg-blue-50'
          : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary'
      } ${busy ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
    >
      {hasVoted ? (
        <HandThumbUpSolid className={iconSize} />
      ) : (
        <HandThumbUpOutline className={iconSize} />
      )}
      <span className="text-xs font-medium tabular-nums">{count}</span>
    </button>
  );
}
